import React, { useLayoutEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import styles from './KPINumber.module.scss';
import Trend from '../Trend/Trend';

export interface KPINumberProps {
  value: string | number | null | undefined;
  label?: string;
  percentage?: number | null;
  smallLabel?: boolean;
  isStatistic?: boolean;
  progressColor?: string;
}

const KPINumber: React.FC<KPINumberProps> = ({
  value,
  label,
  percentage,
  smallLabel = false,
  isStatistic,
  progressColor,
}) => {
  const valueRef = useRef<HTMLParagraphElement>(null);
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    const el = valueRef.current;
    if (!el) return undefined;
    const fit = () => {
      const available = el.parentElement?.clientWidth ?? 0;
      el.style.fontSize = '';
      const needed = el.scrollWidth;
      const basePx = parseFloat(window.getComputedStyle(el).fontSize);
      if (!available || !needed || !basePx) return;
      const next = Math.min(1, Math.max(0.5, available / needed));
      el.style.fontSize = next < 1 ? `${(basePx * next).toFixed(1)}px` : '';
      setScale(next);
    };
    fit();
    document.fonts?.ready.then(fit).catch(() => {});
    // Changing the font size inside the observer callback resizes the observed
    // parent again within the same frame, which the browser reports as
    // "ResizeObserver loop completed with undelivered notifications".
    let frame = 0;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(fit);
    });
    if (el.parentElement) observer.observe(el.parentElement);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [value]);

  return (
    <div
      className={classNames(styles.root, 'flex items-baseline flex-wrap flex-col lg:flex-row gap-y-12', {
        'lg:flex-col': isStatistic,
      })}>
      {value !== null && (
        <p
          ref={valueRef}
          className={classNames('text-full lg:pr-8 tabular-nums whitespace-nowrap max-w-full', {
            'typo-title-24 lg:typo-title-36': !smallLabel,
            'typo-title-24': smallLabel,
          })}
          data-fit-scale={scale < 1 ? scale.toFixed(2) : undefined}>
          {value}
        </p>
      )}
      {(label || (percentage !== null && percentage !== 0)) && (
        <div className="flex items-center flex-shrink-0">
          {label && (
            <p
              className={classNames('text-faded align-bottom pr-4 mb-2', {
                'typo-text-14': !smallLabel,
                'typo-text-12': smallLabel,
              })}>
              {label}
            </p>
          )}
          {percentage && percentage !== 0 && <Trend progressColor={progressColor} percentage={percentage as number} />}
        </div>
      )}
    </div>
  );
};

export default KPINumber;
